// ProblemSection — 4-moment section | native CSS scroll-snap, one 100vh child per moment | Students/Counselors/Institutions/Unity | content is always mounted and opaque; scroll never drives visibility

import React, { useRef, useState, useEffect } from 'react'
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion'
import { ChevronDown } from 'lucide-react'
import { colors, typography } from './landingTokens'

// Fixed navbar height (TripticNav uses h-[64px]) — moments inset their content
// by this so it centers in the VISIBLE area rather than under the nav.
const NAV_HEIGHT = 64

// Stat sub-label — reads as a real line, not a caption. Was 14px @ 0.4 opacity,
// which was too small/faint under the large stat word.
const STAT_SUB_STYLE = {
  fontFamily: 'Inter, sans-serif',
  fontSize: '20px',
  fontWeight: 400,
  lineHeight: 1.5,
  color: 'rgba(255,255,255,0.72)',
} as const

// ──── Types ──────────────────────────────────────────────────────

interface Moment {
  index: number
  portal: string | null
  color: string
  bgTint: string
  stat: string
  statSub: string
  headline: string
  subheading: string
  bullets: string[]
  isTurn?: boolean
}

// ──── Data ───────────────────────────────────────────────────────

const MOMENTS: Moment[] = [
  {
    index: 0,
    portal: 'STUDENTS',
    color: colors.student,
    bgTint: 'rgba(168,85,247,0.06)',
    stat: 'Alone',
    statSub: 'is how most students run their applications',
    headline: 'Essays, deadlines, lists — scattered everywhere.',
    subheading: 'Students juggle essays, deadlines, and college lists across docs, spreadsheets, and chat threads. Nothing lives in one place. Nobody sees the whole picture.',
    bullets: [
      'Essays in one tab, deadlines in another',
      'No single view of what\'s due when',
      'Counselor feedback lost in email threads',
    ],
  },
  {
    index: 1,
    portal: 'COUNSELORS',
    color: colors.counselor,
    bgTint: 'rgba(236,72,153,0.06)',
    stat: 'A dozen',
    statSub: 'tabs to track one student\'s progress',
    headline: 'You can\'t run a caseload from spreadsheets.',
    subheading: 'Every student means another doc, another thread, another deadline to chase. You find out someone\'s behind when it\'s already too late.',
    bullets: [
      'No single dashboard across students',
      'Deadlines slip before you see them',
      'Hours lost chasing status updates',
    ],
  },
  {
    index: 2,
    portal: 'INSTITUTIONS',
    color: colors.college,
    bgTint: 'rgba(251,191,36,0.06)',
    stat: 'Too many',
    statSub: 'of your students leave without reaching their potential abroad',
    headline: 'Your students are applying blind. You have no idea where they land.',
    subheading: 'No data. No feedback. No idea why your students are getting rejected.',
    bullets: [
      'No visibility into where your graduates actually land',
      'No data on why acceptance rates are declining',
      'Parents complaining — and you have nothing to show them',
    ],
  },
  {
    index: 3,
    portal: null,
    color: '#FFFFFF',
    bgTint: 'rgba(255,255,255,0.02)',
    stat: '3',
    statSub: 'broken experiences. one root cause.',
    headline: 'Nobody is working together.',
    subheading:
      'The student is buried in tabs. The counselor is chasing updates. The school can\'t see any of it. Triptic is the shared workspace that fixes all three.',
    bullets: [
      'Students and counselors work in separate tools',
      'Schools have no visibility into outcomes',
      'Three sides. Zero shared infrastructure',
    ],
    isTurn: true,
  },
]

// ──── Static moment (reduced-motion fallback) ────────────────────

interface StaticMomentProps {
  moment: Moment
  onScrollPast: () => void
}

const StaticMoment: React.FC<StaticMomentProps> = ({ moment, onScrollPast }) => (
  <div
    style={{
      padding: '60px 24px',
      borderBottom: `1px solid ${colors.border}`,
      maxWidth: 800,
      margin: '0 auto',
    }}
  >
    {moment.portal && (
      <p style={{ ...typography.label, color: moment.color, marginBottom: 12 }}>{moment.portal}</p>
    )}
    <div
      style={{
        fontFamily: 'Cormorant Garamond, serif',
        fontSize: 'clamp(64px, 12vw, 120px)',
        fontWeight: 800,
        // moment.color is already '#FFFFFF' for moment 3 — no special case needed
        color: moment.color,
        lineHeight: 1.0,
        letterSpacing: '-0.03em',
      }}
    >
      {moment.stat}
    </div>
    <p style={{ ...STAT_SUB_STYLE, marginBottom: 20, marginTop: 4 }}>
      {moment.statSub}
    </p>
    <h2 style={{ ...typography.headingLG, color: colors.textPrimary, marginBottom: 12 }}>
      {moment.headline}
    </h2>
    <p style={{ ...typography.subheading, maxWidth: 480, marginBottom: 24 }}>{moment.subheading}</p>
    {moment.bullets.map((b) => (
      <div key={b} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 10 }}>
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: moment.color,
            marginTop: 6,
            flexShrink: 0,
          }}
        />
        <span
          style={{
            fontSize: 15,
            fontFamily: 'Inter, sans-serif',
            color: colors.textSecondary,
            lineHeight: 1.6,
          }}
        >
          {b}
        </span>
      </div>
    ))}
    {moment.isTurn && (
      <button
        onClick={onScrollPast}
        style={{
          marginTop: 28,
          background: 'white',
          color: '#050508',
          padding: '14px 28px',
          borderRadius: 8,
          fontWeight: 600,
          fontSize: 15,
          fontFamily: 'Inter, sans-serif',
          border: 'none',
          cursor: 'pointer',
        }}
      >
        See how Triptic fixes this →
      </button>
    )}
  </div>
)

// ──── BlinkingCursor ──────────────────────────────────────────────

const BlinkingCursor: React.FC = () => (
  <>
    <style>{`@keyframes ps-blink{0%,49%{opacity:1}50%,100%{opacity:0}}`}</style>
    <div
      style={{
        width: 2,
        height: 14,
        background: '#a855f7',
        display: 'inline-block',
        animation: 'ps-blink 0.7s infinite',
        verticalAlign: 'text-bottom',
      }}
    />
  </>
)

// ──── FloatIndicator ─────────────────────────────────────────────

interface FloatIndicatorProps {
  color: string
  text: string
  delay?: number
}

const FloatIndicator: React.FC<FloatIndicatorProps> = ({ color, text, delay = 0 }) => (
  <motion.div
    style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}
    animate={{ y: [0, -5, 0] }}
    transition={{ duration: 3, ease: 'easeInOut', repeat: Infinity, delay }}
  >
    <div
      style={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: color,
        flexShrink: 0,
      }}
    />
    <div
      style={{
        width: 40,
        height: 1,
        background: color,
        opacity: 0.4,
        flexShrink: 0,
      }}
    />
    <span
      style={{
        fontSize: 11,
        fontFamily: 'Inter, sans-serif',
        color,
        fontWeight: 500,
        whiteSpace: 'nowrap',
      }}
    >
      {text}
    </span>
  </motion.div>
)

// ──── Visual 0 — Student ─────────────────────────────────────────

const Visual0Student: React.FC<{ isMobile?: boolean }> = ({ isMobile }) => (
  <div style={{ position: 'relative', width: 280 }}>
    <motion.div
      animate={{ y: [0, -8, 0] }}
      transition={{ duration: 3, ease: 'easeInOut', repeat: Infinity }}
      style={{
        background: 'rgba(12,4,24,0.95)',
        border: '1px solid rgba(168,85,247,0.5)',
        borderRadius: 16,
        padding: '20px 20px 16px',
        filter: 'drop-shadow(0 0 30px rgba(168,85,247,0.2))',
        position: 'relative',
        fontFamily: 'Inter, sans-serif',
      }}
    >
      {/* Left accent border */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 16,
          bottom: 16,
          width: 3,
          borderRadius: '0 2px 2px 0',
          background: '#a855f7',
        }}
      />

      {/* Title line */}
      <div
        style={{
          height: 3,
          width: '80%',
          background: 'rgba(168,85,247,0.7)',
          borderRadius: 2,
          marginBottom: 16,
          marginLeft: 12,
        }}
      />

      {/* Text lines with cursor after line 2 */}
      {[100, 80, 100, 65, 90, 50].map((w, i) => (
        <React.Fragment key={i}>
          <div
            style={{
              height: 2,
              width: `${w}%`,
              marginLeft: 12,
              background: 'rgba(255,255,255,0.18)',
              borderRadius: 1,
              marginBottom: 8,
            }}
          />
          {i === 1 && (
            <div style={{ marginLeft: 12, marginBottom: 8 }}>
              <BlinkingCursor />
            </div>
          )}
        </React.Fragment>
      ))}

      {/* Divider */}
      <div style={{ height: 1, background: 'rgba(168,85,247,0.1)', margin: '12px 0' }} />

      {/* DEADLINE */}
      <div
        style={{
          fontSize: 9,
          fontWeight: 600,
          letterSpacing: '0.12em',
          color: 'rgba(168,85,247,0.6)',
          marginBottom: 8,
          marginLeft: 12,
        }}
      >
        DEADLINE
      </div>
      <div
        style={{
          background: 'rgba(239,68,68,0.08)',
          border: '1px solid rgba(239,68,68,0.3)',
          borderRadius: 6,
          padding: '8px 12px',
          marginLeft: 12,
          marginBottom: 12,
        }}
      >
        <span style={{ fontSize: 12, color: 'rgba(239,68,68,0.9)', fontWeight: 500 }}>
          ⚠ Stanford — 3 days left
        </span>
      </div>

      {/* COUNSELOR */}
      <div
        style={{
          fontSize: 9,
          fontWeight: 600,
          letterSpacing: '0.12em',
          color: 'rgba(168,85,247,0.5)',
          margin: '0 0 6px 12px',
        }}
      >
        COUNSELOR
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 12 }}>
        <div
          style={{
            width: 24,
            height: 24,
            borderRadius: '50%',
            background: 'rgba(168,85,247,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 9,
            color: '#a855f7',
            fontWeight: 600,
          }}
        >
          SC
        </div>
        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>Last seen 2 weeks ago</span>
      </div>

      {/* Word count */}
      <div
        style={{
          fontSize: 10,
          color: 'rgba(255,255,255,0.15)',
          textAlign: 'right',
          marginTop: 10,
        }}
      >
        0 / 650 words
      </div>
    </motion.div>

    {/* Right floating indicators — hidden on mobile (bleed off screen) */}
    {!isMobile && (
      <div style={{ position: 'absolute', right: -130, top: '25%' }}>
        <FloatIndicator color="rgba(239,68,68,0.85)" text="Stanford: 3 days" delay={0} />
        <FloatIndicator color="rgba(168,85,247,0.7)" text="0 words written" delay={0.5} />
      </div>
    )}
  </div>
)

// ──── Visual 1 — Counselor ───────────────────────────────────────

const Visual1Counselor: React.FC = () => {
  const getDepthStyle = (i: number): React.CSSProperties => {
    if (i >= 5)
      return { background: 'rgba(8,2,10,0.4)', border: '1px solid rgba(236,72,153,0.08)' }
    if (i >= 2)
      return { background: 'rgba(16,4,18,0.75)', border: '1px solid rgba(236,72,153,0.2)' }
    if (i === 1)
      return { background: 'rgba(20,4,18,0.9)', border: '1px solid rgba(236,72,153,0.35)' }
    return {
      background: 'rgba(22,4,20,0.98)',
      border: '1px solid rgba(236,72,153,0.8)',
      filter: 'drop-shadow(0 0 20px rgba(236,72,153,0.3))',
    }
  }

  return (
    <div style={{ position: 'relative', width: 320, height: 320 }}>
      {/* Above-stack label */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          textAlign: 'center',
          fontSize: 11,
          fontFamily: 'Inter, sans-serif',
          color: 'rgba(236,72,153,0.4)',
        }}
      >
        +47 pending reviews
      </div>

      {/* Stack container */}
      <div style={{ position: 'relative', width: 280, height: 260, margin: '28px auto 0' }}>
        {Array.from({ length: 8 }, (_, i) => i)
          .reverse()
          .map((i) => (
            <div
              key={i}
              style={{
                position: 'absolute',
                left: '50%',
                width: 280 + i * 4,
                height: 80,
                bottom: i * 36,
                transform: `translateX(-50%) rotate(${(i - 3) * 1.5}deg)`,
                borderRadius: 10,
                overflow: 'hidden',
                ...getDepthStyle(i),
              }}
            >
              {i === 0 && (
                <>
                  {/* Pink header line */}
                  <div
                    style={{
                      height: 3,
                      width: '100%',
                      background: 'rgba(236,72,153,0.6)',
                    }}
                  />
                  {/* Name + status */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '8px 12px 0',
                      fontFamily: 'Inter, sans-serif',
                    }}
                  >
                    <span style={{ fontSize: 14, fontWeight: 600, color: 'rgba(255,255,255,0.9)' }}>
                      Sarah K.
                    </span>
                    <span style={{ fontSize: 10, color: 'rgba(236,72,153,0.7)' }}>
                      PENDING REVIEW
                    </span>
                  </div>
                  {/* Content lines */}
                  <div
                    style={{
                      height: 2,
                      margin: '8px 12px 0',
                      background: 'rgba(255,255,255,0.12)',
                      borderRadius: 1,
                      width: '85%',
                    }}
                  />
                  <div
                    style={{
                      height: 2,
                      margin: '6px 12px 0',
                      background: 'rgba(255,255,255,0.08)',
                      borderRadius: 1,
                      width: '60%',
                    }}
                  />
                </>
              )}
            </div>
          ))}
      </div>
    </div>
  )
}

// ──── Visual 2 — School ──────────────────────────────────────────

const Visual2School: React.FC = () => {
  const trendBars = [
    { year: '2021', pct: 72, opacity: 1.0 },
    { year: '2022', pct: 65, opacity: 0.8 },
    { year: '2023', pct: 58, opacity: 0.65 },
    { year: '20XX', pct: 51, opacity: 0.5 },
  ]
  const questions = [
    'Where do our graduates go?',
    'Which colleges reject us most?',
    'Why is yield dropping?',
  ]
  const complaints = [
    { text: '"Why was my child rejected from a safety?"', border: 'rgba(239,68,68,0.6)' },
    { text: '"No one told us about this college."',       border: 'rgba(251,146,60,0.6)' },
    { text: '"Counselor never followed up with us."',     border: 'rgba(230,168,23,0.4)' },
  ]
  const sectionLbl = {
    fontSize: 9,
    fontWeight: 600,
    letterSpacing: '0.12em',
    textTransform: 'uppercase' as const,
    color: 'rgba(230,168,23,0.5)',
    marginBottom: 8,
  }
  const divider = { height: 1, background: 'rgba(255,255,255,0.06)', margin: '12px 0' }

  return (
    <div style={{ width: 280, fontFamily: 'Inter, sans-serif' }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'rgba(230,168,23,0.6)' }}>
          Principal View · Class of 20XX · 847 Students
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 4 }}>
        <motion.div
          style={{ width: 6, height: 6, borderRadius: '50%', background: 'rgba(239,68,68,0.9)', flexShrink: 0 }}
          animate={{ opacity: [1, 0.3, 1] }}
          transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
        />
        <span style={{ fontSize: 10, color: 'rgba(239,68,68,0.8)' }}>End of Cycle</span>
      </div>

      {/* Divider */}
      <div style={divider} />

      {/* Unanswered Questions */}
      <div style={sectionLbl}>Unanswered Questions</div>
      {questions.map((q) => (
        <div key={q} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>{q}</span>
          <span style={{
            background: 'rgba(239,68,68,0.1)',
            border: '1px solid rgba(239,68,68,0.3)',
            borderRadius: 4,
            padding: '2px 8px',
            fontSize: 10,
            color: 'rgba(239,68,68,0.8)',
            whiteSpace: 'nowrap',
            marginLeft: 8,
          }}>
            No visibility
          </span>
        </div>
      ))}

      {/* Divider */}
      <div style={divider} />

      {/* Acceptance Rate Trend */}
      <div style={sectionLbl}>Acceptance Rate — 4 Year Trend</div>
      {trendBars.map((bar, i) => (
        <div key={bar.year} style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>{bar.year}</span>
            <span style={{ fontSize: 10, color: `rgba(230,168,23,${bar.opacity})` }}>{bar.pct}%</span>
          </div>
          <div style={{ height: 6, borderRadius: 4, background: 'rgba(255,255,255,0.05)', overflow: 'hidden' }}>
            <motion.div
              initial={{ width: 0 }}
              whileInView={{ width: `${bar.pct}%` }}
              viewport={{ once: true }}
              transition={{ duration: 1.2, delay: i * 0.15, ease: 'easeOut' }}
              style={{
                height: '100%',
                background: `rgba(230,168,23,${bar.opacity})`,
                borderRadius: 4,
              }}
            />
          </div>
        </div>
      ))}

      {/* Divider */}
      <div style={divider} />

      {/* Parent Complaints */}
      <div style={sectionLbl}>Parent Complaints This Cycle</div>
      {complaints.map((c) => (
        <div
          key={c.text}
          style={{
            borderLeft: `2px solid ${c.border}`,
            paddingLeft: 8,
            marginBottom: 8,
          }}
        >
          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', fontStyle: 'italic' }}>{c.text}</span>
        </div>
      ))}

      {/* Bottom warning bar */}
      <div
        style={{
          background: 'rgba(239,68,68,0.06)',
          border: '1px solid rgba(239,68,68,0.2)',
          borderRadius: 6,
          padding: '8px 12px',
          marginTop: 4,
        }}
      >
        <span style={{ fontSize: 11, color: 'rgba(239,68,68,0.75)' }}>
          ⚠ 0 structured outcome data collected this year
        </span>
      </div>
    </div>
  )
}

// ──── Visual 3 — The Turn ────────────────────────────────────────

const Visual3Turn: React.FC = () => {
  const portals = [
    { name: 'Students', color: '#a855f7', cx: 150, cy: 60 },
    { name: 'Counselors', color: '#ec4899', cx: 50, cy: 240 },
    { name: 'Schools', color: '#e6a817', cx: 250, cy: 240 },
  ]
  const pairs: [number, number][] = [[0, 1], [1, 2], [0, 2]]
  const drifts = [
    { x: [0, 6, 0, -6, 0] as number[], y: [0, -6, 0, 6, 0] as number[], duration: 4 },
    { x: [0, -5, 0, 5, 0] as number[], y: [0, 6, 0, -6, 0] as number[], duration: 5 },
    { x: [0, 4, 0, -4, 0] as number[], y: [0, -5, 0, 5, 0] as number[], duration: 6 },
  ]

  return (
    <div style={{ position: 'relative', width: 300, height: 320 }}>
      {/* SVG connection lines at static positions */}
      <svg
        width="300"
        height="320"
        style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
      >
        {pairs.map(([a, b], idx) => {
          const ax = portals[a].cx, ay = portals[a].cy
          const bx = portals[b].cx, by = portals[b].cy
          const mx = (ax + bx) / 2, my = (ay + by) / 2
          return (
            <g key={idx}>
              <line
                x1={ax} y1={ay} x2={bx} y2={by}
                stroke="rgba(255,255,255,0.08)"
                strokeWidth="1"
                strokeDasharray="4 8"
              />
              <line
                x1={mx - 8} y1={my - 8} x2={mx + 8} y2={my + 8}
                stroke="rgba(239,68,68,0.5)"
                strokeWidth="1.5"
              />
              <line
                x1={mx + 8} y1={my - 8} x2={mx - 8} y2={my + 8}
                stroke="rgba(239,68,68,0.5)"
                strokeWidth="1.5"
              />
            </g>
          )
        })}
      </svg>

      {/* Portal circles */}
      {portals.map((p, i) => (
        <motion.div
          key={i}
          style={{
            position: 'absolute',
            left: p.cx - 48,
            top: p.cy - 48,
            width: 96,
            height: 96,
            borderRadius: '50%',
            border: `1.5px solid ${p.color}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            filter: `drop-shadow(0 0 16px ${p.color}22)`,
          }}
          animate={{ x: drifts[i].x, y: drifts[i].y }}
          transition={{
            duration: drifts[i].duration,
            ease: 'easeInOut',
            repeat: Infinity,
          }}
        >
          <span
            style={{
              fontSize: 11,
              fontWeight: 500,
              fontFamily: 'Inter, sans-serif',
              color: p.color,
              opacity: 0.85,
              textAlign: 'center',
            }}
          >
            {p.name}
          </span>
        </motion.div>
      ))}

      {/* Footer */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          textAlign: 'center',
          fontSize: 13,
          fontFamily: 'Inter, sans-serif',
          fontWeight: 600,
          letterSpacing: '0.05em',
          color: 'rgba(255,255,255,0.7)',
        }}
      >
        No shared infrastructure
      </div>
    </div>
  )
}

// ──── RightVisual (replaces ThreadVisual) ────────────────────────

interface RightVisualProps {
  activeIndex: number
  isMobile?: boolean
}

const RightVisual: React.FC<RightVisualProps> = ({ activeIndex, isMobile }) => {
  const prefersReduced = useReducedMotion()

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        maxWidth: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={activeIndex}
          initial={prefersReduced ? {} : { opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={prefersReduced ? {} : { opacity: 0, scale: 1.02 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        >
          {activeIndex === 0 ? <Visual0Student isMobile={isMobile} />
            : activeIndex === 1 ? <Visual1Counselor />
            : activeIndex === 2 ? <Visual2School />
            : <Visual3Turn />}
        </motion.div>
      </AnimatePresence>
    </div>
  )
}

// ──── Main component ─────────────────────────────────────────────

function ProblemSection() {
  const sectionRef = useRef<HTMLElement>(null)
  const momentRefs = useRef<(HTMLDivElement | null)[]>([])
  const [activeIndex, setActiveIndex] = useState(0)
  const [hasScrolled, setHasScrolled] = useState(false)
  const [sectionInView, setSectionInView] = useState(false)
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  )

  // Reactive so an OS-level change takes effect without a reload
  const prefersReduced = !!useReducedMotion()

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Progress dots follow the snapped moment. Read-only — never drives visibility,
  // so a missed callback can never blank a moment out.
  useEffect(() => {
    if (isMobile || prefersReduced) return
    const nodes = momentRefs.current.filter(Boolean) as HTMLDivElement[]
    if (!nodes.length) return

    // Track which moments are currently ≥50% visible. The overlays (dots + hint)
    // are position:fixed, so they must show ONLY while a moment actually occupies
    // the viewport — gating on the ~400vh section instead let them leak over
    // HowItWorks and neighbours.
    const visible = new Set<number>()

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const i = Number((entry.target as HTMLElement).dataset.momentIndex)
          if (Number.isNaN(i)) return

          if (entry.isIntersecting) {
            visible.add(i)
            setActiveIndex(i)
            // Mirror for the wheel listener — same source of truth as the state
            if (i > 0) setHasScrolled(true)
          } else {
            visible.delete(i)
          }
        })
        setSectionInView(visible.size > 0)
      },
      {
        // Discount the navbar strip so a sliver under the nav doesn't count
        rootMargin: `-${NAV_HEIGHT}px 0px 0px 0px`,
        threshold: 0.5,
      }
    )

    nodes.forEach((n) => observer.observe(n))
    return () => {
      observer.disconnect()
      setSectionInView(false)
    }
  }, [isMobile, prefersReduced])

  // Scrolling is fully native: CSS scroll-snap on body.landing-snap plus
  // scroll-snap-align/stop on the 4 children. Nothing intercepts the wheel, so
  // the feel adapts to input speed while frames still settle cleanly.

  // Dots scroll the page to a moment — no internal index state to desync
  const scrollToMoment = (i: number) => {
    momentRefs.current[i]?.scrollIntoView({
      behavior: prefersReduced ? 'auto' : 'smooth',
      block: 'start',
    })
  }

  // Lands on the narrative beat — the answer to these problem moments.
  // `start`, not `center`: that section is a tall sticky scroll track, so
  // centering it would drop the user midway through the reveal.
  const scrollPastSection = () => {
    const next = sectionRef.current?.nextElementSibling as HTMLElement | null
    if (next) {
      next.scrollIntoView({
        behavior: prefersReduced ? 'auto' : 'smooth',
        block: 'start',
      })
    }
  }

  // ── Reduced-motion: static vertical stack ──
  if (prefersReduced) {
    return (
      <section
        id="problem-section"
        ref={sectionRef}
        style={{ background: 'transparent', paddingTop: 80, paddingBottom: 80 }}
      >
        {MOMENTS.map((m) => (
          <StaticMoment key={m.index} moment={m} onScrollPast={scrollPastSection} />
        ))}
      </section>
    )
  }

  // ── Mobile: simple stacked scroll, one moment per screen ──
  if (isMobile) {
    return (
      <section id="problem-section" style={{ position: 'relative' }}>
        {MOMENTS.map((m) => (
          <motion.div
            key={m.index}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-10%' }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            style={{
              minHeight: '100svh',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              padding: '80px 24px 40px 24px',
              position: 'relative',
            }}
          >
            {/* Portal label */}
            {m.portal && (
              <p style={{ ...typography.label, color: m.color, marginBottom: 16 }}>
                {m.portal}
              </p>
            )}

            {/* Stat number — shown for all four moments. Moment 3 used to be
                skipped here, leaving that beat without the series' anchor. */}
            <div style={{
              fontFamily: 'Cormorant Garamond, serif',
              fontSize: 'clamp(64px, 16vw, 120px)',
              fontWeight: 800,
              color: m.color,
              lineHeight: 1.2,
              letterSpacing: '-0.03em',
              marginBottom: 12,
            }}>
              {m.stat}
            </div>

            {/* Stat sub-label */}
            <p style={{ ...STAT_SUB_STYLE, marginBottom: 24, marginTop: 8 }}>
              {m.statSub}
            </p>

            {/* Headline */}
            <h2 style={{ ...typography.headingLG, fontSize: 'clamp(24px, 3vw, 48px)', color: colors.textPrimary, marginBottom: 16 }}>
              {m.headline}
            </h2>

            {/* Subheading */}
            <p style={{ ...typography.subheading, maxWidth: 480, marginBottom: 32 }}>
              {m.subheading}
            </p>

            {/* Bullets */}
            {m.bullets.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 32 }}>
                {m.bullets.map((b) => (
                  <div key={b} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: m.color, flexShrink: 0, marginTop: 6 }} />
                    <span style={{ fontSize: 15, fontFamily: 'Inter, sans-serif', color: colors.textSecondary, lineHeight: 1.6 }}>
                      {b}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* CTA (moment 3 only) */}
            {m.isTurn && (
              <button
                onClick={scrollPastSection}
                style={{
                  marginTop: 32,
                  background: 'white',
                  color: '#050508',
                  padding: '14px 32px',
                  borderRadius: 8,
                  fontWeight: 700,
                  fontSize: 15,
                  fontFamily: 'Inter, sans-serif',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                See how Triptic fixes this →
              </button>
            )}

            {/* Visual card — floating annotations hidden via isMobile prop */}
            <div style={{ width: '100%', overflow: 'hidden', marginTop: 40 }}>
              <RightVisual activeIndex={m.index} isMobile={true} />
            </div>
          </motion.div>
        ))}
      </section>
    )
  }

  // ── Left content for one moment (desktop) ──
  // Always mounted and fully opaque — visibility is never animation-driven,
  // so no scroll position or interrupted transition can blank it out.
  const renderLeftContent = (moment: Moment) => (
      <div
        style={{
          position: 'relative',
        }}
      >
        {/* Portal label */}
        {moment.portal && (
          <p style={{ ...typography.label, color: moment.color, marginBottom: 16 }}>
            {moment.portal}
          </p>
        )}

        {/* Stat number — identical treatment for all four moments. Moment 3
            previously rendered as a faint absolute-positioned watermark, which
            pulled it out of flow and made the whole beat read as a different
            kind of section. */}
        <div
          style={{
            fontFamily: 'Cormorant Garamond, serif',
            fontSize: 'clamp(64px, 10vw, 140px)',
            fontWeight: 800,
            color: moment.color,
            lineHeight: 1.2,
            letterSpacing: '-0.03em',
            marginBottom: 12,
          }}
        >
          {moment.stat}
        </div>

        {/* Stat sub-label */}
        <p
          style={{
            ...STAT_SUB_STYLE,
            marginBottom: 24,
            marginTop: 8,
          }}
        >
          {moment.statSub}
        </p>

        {/* Headline */}
        <h2
          style={{
            ...typography.headingLG,
            fontSize: 'clamp(24px, 3vw, 48px)',
            color: colors.textPrimary,
            marginBottom: 16,
          }}
        >
          {moment.headline}
        </h2>

        {/* Subheading */}
        <p style={{ ...typography.subheading, maxWidth: 480, marginBottom: 32 }}>
          {moment.subheading}
        </p>

        {/* Bullets (moments 0–2) */}
        {moment.bullets.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {moment.bullets.map((b) => (
              <div key={b} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: moment.color,
                    flexShrink: 0,
                    marginTop: 6,
                  }}
                />
                <span
                  style={{
                    fontSize: 15,
                    fontFamily: 'Inter, sans-serif',
                    color: colors.textSecondary,
                    lineHeight: 1.6,
                  }}
                >
                  {b}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* CTA (moment 3 only) */}
        {moment.isTurn && (
          <button
            onClick={scrollPastSection}
            style={{
              marginTop: 32,
              background: 'white',
              color: '#050508',
              padding: '14px 32px',
              borderRadius: 8,
              fontWeight: 700,
              fontSize: 15,
              fontFamily: 'Inter, sans-serif',
              border: 'none',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              transition: 'background 200ms ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.88)' }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'white' }}
          >
            See how Triptic fixes this →
          </button>
        )}
      </div>
  )

  // ── Desktop: native CSS scroll-snap, one 100vh child per moment ──
  return (
    <section
      id="problem-section"
      ref={sectionRef}
      style={{
        position: 'relative',
        margin: 0,
        padding: 0,
      }}
    >
      {MOMENTS.map((m, i) => (
        <div
          key={m.index}
          ref={(el) => { momentRefs.current[i] = el }}
          data-moment-index={i}
          style={{
            position: 'relative',
            height: '100vh',
            // Fully native snapping. `scroll-snap-stop` is deliberately NOT set:
            // it forces a hard stop at every snap point, which is what made the
            // scroll feel mechanical. `mandatory` alone still settles each frame
            // without straddling, while staying adaptive to input speed.
            scrollSnapAlign: 'start',
          }}
        >
          {/* LEFT — content centered within the visible area below the navbar */}
          <div
            style={{
              position: 'absolute',
              left: '6%',
              top: NAV_HEIGHT,
              bottom: 0,
              width: '45%',
              zIndex: 2,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            {renderLeftContent(m)}
          </div>

          {/* RIGHT — visual, centered in the same navbar-inset area */}
          <div
            style={{
              position: 'absolute',
              right: 0,
              top: NAV_HEIGHT,
              bottom: 0,
              width: '52%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '24px 40px 24px 0',
              overflow: 'hidden',
            }}
          >
            {/* Ambient float on a wrapper — keeps it clear of RightVisual's own
                enter/exit animation and of the section's scroll-snap. Each
                moment gets a different cycle offset so they don't feel
                mechanical. */}
            <motion.div
              animate={prefersReduced ? {} : { y: [0, -10, 0] }}
              transition={
                prefersReduced
                  ? { duration: 0 }
                  : {
                      duration: 7,
                      ease: 'easeInOut',
                      repeat: Infinity,
                      repeatType: 'reverse',
                      delay: i * 0.9,
                    }
              }
              style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <RightVisual activeIndex={m.index} />
            </motion.div>
          </div>
        </div>
      ))}

      {/* Overlays — viewport-fixed, shown only while the section is in view.
          No negative margins or zero-height sticky: those pulled a 100vh box out
          of flow and stacked this section over the next one. */}
      {sectionInView && (
        <>
        {/* Progress dots — right edge, desktop only */}
        <div
          style={{
            position: 'fixed',
            right: 32,
            top: '50%',
            transform: 'translateY(-50%)',
            display: isMobile ? 'none' : 'flex',
            flexDirection: 'column',
            gap: 12,
            zIndex: 20,
            pointerEvents: 'auto',
          }}
        >
          {MOMENTS.map((m, i) => (
            <button
              key={i}
              aria-label={`Go to moment ${i + 1}`}
              onClick={() => scrollToMoment(i)}
              style={{
                width: i === activeIndex ? 8 : 6,
                height: i === activeIndex ? 8 : 6,
                borderRadius: i === activeIndex ? 4 : 3,
                background: i === activeIndex ? m.color : 'rgba(255,255,255,0.2)',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                transition: 'all 200ms',
                flexShrink: 0,
              }}
            />
          ))}
        </div>

        {/* Scroll hint — moment 0 only, fades on scroll */}
        <AnimatePresence>
          {activeIndex === 0 && !hasScrolled && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              style={{
                position: 'fixed',
                bottom: 32,
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 6,
                zIndex: 20,
                pointerEvents: 'none',
              }}
            >
              <span style={{ ...typography.caption }}>Scroll to see more</span>
              <motion.div
                animate={prefersReduced ? {} : { y: [0, 6, 0] }}
                transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
              >
                <ChevronDown size={16} color={colors.textMuted} />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        </>
      )}
    </section>
  )
}

export default ProblemSection
